import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, Heart, MessageCircle } from 'lucide-react';
import { contactInfo } from '../utils/mockData';

/**
 * "It Starts With a Moment" - cinematic scrollable ad page.
 * One scene per scroll-snap section. Ken Burns motion + scroll-triggered text reveals.
 *
 * Each scene = a full-viewport image with subtle zoom/pan + a layered text block
 * that fades + slides in once the section enters the viewport.
 */

const SCENES = [
  {
    id: 'opener',
    image: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&w=1920&q=85',
    eyebrow: '',
    line1: 'It starts with a moment…',
    line2: 'one you don’t realise will mean everything.',
    align: 'center',
    overlay: 'from-black/30 via-black/50 to-black/85',
  },
  {
    id: 'fragments',
    image: 'https://images.unsplash.com/photo-1529634597503-139d3726fed5?auto=format&fit=crop&w=1920&q=85',
    line1: 'A look. A laugh. A goodbye. A beginning.',
    align: 'center',
    overlay: 'from-black/40 via-black/55 to-black/80',
    lineSize: 'text-3xl md:text-5xl lg:text-6xl',
  },
  {
    id: 'pass',
    image: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=85',
    line1: 'Moments that pass in seconds…',
    line2: 'but stay with you forever.',
    align: 'center',
    overlay: 'from-black/50 via-black/55 to-black/85',
  },
  {
    id: 'capture',
    image: 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=1920&q=85',
    line1: 'Photos capture them…',
    line2: 'but stories bring them back to life.',
    align: 'center',
    overlay: 'from-black/40 via-black/55 to-black/80',
  },
  {
    id: 'transform',
    image: 'https://images.unsplash.com/photo-1542037104857-ffbb0b9155fb?auto=format&fit=crop&w=1920&q=85',
    eyebrow: 'AT VISUAL CREATIONZ,',
    line1: 'we transform your memories',
    line2: 'into something you can feel… again and again.',
    align: 'center',
    overlay: 'from-black/55 via-black/60 to-black/85',
    lineSize: 'text-3xl md:text-5xl lg:text-6xl',
  },
  {
    id: 'closing',
    image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1920&q=85',
    line1: 'Because the moments that matter…',
    line2: 'deserve to live on.',
    align: 'center',
    overlay: 'from-black/55 via-black/65 to-black/95',
  },
];

const Scene = ({ scene, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => setVisible(e.isIntersecting && e.intersectionRatio > 0.4)),
      { threshold: [0, 0.4, 0.6, 1] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid={`scene-${scene.id}`}
      className="relative h-screen w-full overflow-hidden snap-start flex items-center justify-center"
    >
      {/* Background image with slow Ken Burns motion */}
      <div
        className={`absolute inset-0 bg-cover bg-center transition-[transform,opacity] duration-[8000ms] ease-out ${
          visible ? 'scale-110 opacity-100' : 'scale-100 opacity-90'
        }`}
        style={{ backgroundImage: `url(${scene.image})` }}
      />
      {/* Layered overlay for legibility */}
      <div className={`absolute inset-0 bg-gradient-to-b ${scene.overlay}`} />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,0,0,0.0)_0%,_rgba(0,0,0,0.55)_100%)]" />
      {/* Subtle film grain */}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'200\' height=\'200\'><filter id=\'n\'><feTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\'/></svg>")' }} />

      {/* Text block */}
      <div className={`relative z-10 max-w-4xl mx-auto px-6 text-center transition-all duration-[1400ms] ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}>
        {scene.eyebrow && (
          <p className="uppercase tracking-[0.3em] text-xs md:text-sm text-amber-300/90 mb-6 md:mb-8 font-medium">
            {scene.eyebrow}
          </p>
        )}
        <h2 className={`${scene.lineSize || 'text-4xl md:text-6xl lg:text-7xl'} font-light text-white leading-[1.15] tracking-tight`}
            style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}>
          {scene.line1}
        </h2>
        {scene.line2 && (
          <p
            className={`${scene.lineSize || 'text-2xl md:text-3xl lg:text-4xl'} font-light text-gray-200/90 leading-[1.4] mt-4 md:mt-6 transition-all duration-[1400ms] delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ textShadow: '0 4px 30px rgba(0,0,0,0.6)' }}
          >
            {scene.line2}
          </p>
        )}
      </div>

      {/* Scroll cue on the very first scene */}
      {index === 0 && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/70 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-xs uppercase tracking-[0.3em]">Scroll</span>
          <ArrowDown size={20} />
        </div>
      )}
    </section>
  );
};

const OurStoryPage = () => {
  // Hide global Header / Footer / floating buttons for an immersive, full-bleed feel
  useEffect(() => {
    document.body.classList.add('cinematic-mode');
    // Preload all scene images so transitions are seamless
    SCENES.forEach((s) => {
      const img = new Image();
      img.src = s.image;
    });
    return () => document.body.classList.remove('cinematic-mode');
  }, []);

  // Build WhatsApp link (UK E.164)
  const raw = (contactInfo.whatsapp || '').replace(/\D/g, '');
  const e164 = raw.startsWith('0') ? `44${raw.slice(1)}` : raw;
  const waHref = `https://wa.me/${e164}?text=${encodeURIComponent("Hi Visual Creationz, I'd love to learn more about your services.")}`;

  return (
    <div className="bg-black text-white">
      {/* Scroll snap container */}
      <div className="snap-y snap-mandatory overflow-y-auto h-screen scroll-smooth" data-testid="our-story-scroller">
        {SCENES.map((scene, i) => (
          <Scene key={scene.id} scene={scene} index={i} />
        ))}

        {/* Final CTA scene */}
        <section
          data-testid="scene-cta"
          className="relative h-screen w-full snap-start flex items-center justify-center overflow-hidden bg-gradient-to-b from-black via-gray-950 to-black"
        >
          <div className="absolute inset-0 opacity-30"
               style={{ background: 'radial-gradient(ellipse at center, rgba(245, 158, 11, 0.25) 0%, transparent 60%)' }} />
          <div className="relative z-10 text-center px-6 max-w-3xl">
            <img
              src="https://customer-assets.emergentagent.com/job_slideshow-studio-5/artifacts/3f84c0r9_file_00000000e84c71f4bc5168c8fafe2f26.png"
              alt="Visual Creationz"
              className="h-32 md:h-44 w-auto mx-auto mb-8 [filter:brightness(1.7)_contrast(1.2)_saturate(1.5)_drop-shadow(0_0_18px_rgba(34,211,238,0.5))]"
            />
            <p className="uppercase tracking-[0.4em] text-sm text-amber-300/80 mb-3">Visual Creationz</p>
            <h2 className="text-3xl md:text-5xl font-light text-white mb-10 leading-tight">
              Your memories,<br />beautifully brought to life.
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/pricing"
                data-testid="our-story-order-btn"
                className="inline-flex items-center justify-center gap-2 px-9 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 hover:scale-105 active:scale-95 transition-all shadow-2xl shadow-amber-500/30"
              >
                <Heart size={18} fill="currentColor" />
                Start Your Story
              </Link>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="our-story-message-btn"
                className="inline-flex items-center justify-center gap-2 px-9 py-4 border border-white/30 text-white font-semibold rounded-full hover:bg-white hover:text-black transition-all"
              >
                <MessageCircle size={18} />
                Message Us Today
              </a>
            </div>
            <Link
              to="/"
              className="inline-block mt-10 text-sm text-gray-400 hover:text-white transition-colors"
            >
              ← Back to website
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OurStoryPage;
